/**
 * AssessmentModal — create / edit an income-tax (ITR) assessment for a customer.
 *
 * Single-step form (FY, AY, ITR type, regime, due date). On submit it calls the
 * assessments service and reports the saved assessment back via `onSuccess`.
 * Follows the inline-modal pattern from CustomerModal (DESIGN.md §6.4).
 */

import { useEffect, useState } from 'react'
import { X, AlertCircle, Check } from 'lucide-react'
import { getAssessmentService } from '@/services/assessments'
import { toast } from '@/store/toastStore'
import { ITR_TYPES } from '@/types/assessment.types'
import type {
  Assessment,
  ITRType,
  TaxRegime,
} from '@/types/assessment.types'
import { useAuth } from '@/contexts/AuthContext'

interface AssessmentModalProps {
  isOpen: boolean
  onClose: () => void
  /** Customer the assessment belongs to. */
  customerId: string
  customerPan: string
  /** When provided, the modal edits this assessment instead of creating one. */
  assessment?: Assessment | null
  onSuccess?: (assessment: Assessment) => void
}

const FY_REGEX = /^(20\d{2})-(2\d|[3-9]\d)$/

/** Derive the assessment year ("2025-26") from a financial year ("2024-25"). */
function deriveAssessmentYear(fy: string): string {
  const match = FY_REGEX.exec(fy)
  if (!match) return ''
  const start = Number(match[1]) + 1
  return `${start}-${(start + 1).toString().slice(-2)}`
}

/** Build a list of recent financial years for the select. */
function recentFinancialYears(count = 6): string[] {
  const out: string[] = []
  const current = new Date().getFullYear()
  for (let i = 0; i < count; i++) {
    const start = current - i - 1
    out.push(`${start}-${(start + 1).toString().slice(-2)}`)
  }
  return out
}

interface FormErrors {
  financialYear?: string
  itrType?: string
}

export default function AssessmentModal({
  isOpen,
  onClose,
  customerId,
  customerPan,
  assessment,
  onSuccess,
}: AssessmentModalProps) {
  const isEdit = Boolean(assessment)
  const { hasPermission } = useAuth()
  const canSubmit = hasPermission(
    isEdit ? 'txh.assessment.update' : 'txh.assessment.create'
  )

  const [financialYear, setFinancialYear] = useState('')
  const [itrType, setItrType] = useState<ITRType>('ITR-1')
  const [taxRegime, setTaxRegime] = useState<TaxRegime>('new')
  const [dueDate, setDueDate] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Set<string>>(new Set())
  const [isSaving, setIsSaving] = useState(false)

  // Seed/reset form whenever the modal opens or the target assessment changes.
  useEffect(() => {
    if (!isOpen) return
    const fyOptions = recentFinancialYears()
    setFinancialYear(assessment?.financialYear ?? fyOptions[0] ?? '')
    setItrType(assessment?.itrType ?? 'ITR-1')
    setTaxRegime(assessment?.taxRegime ?? 'new')
    setDueDate(assessment?.dueDate ? assessment.dueDate.slice(0, 10) : '')
    setErrors({})
    setTouched(new Set())
  }, [isOpen, assessment])

  if (!isOpen || !canSubmit) return null

  const assessmentYear = deriveAssessmentYear(financialYear)

  const validate = (): FormErrors => {
    const next: FormErrors = {}
    if (!financialYear || !FY_REGEX.test(financialYear)) {
      next.financialYear = 'Select a valid financial year'
    }
    if (!ITR_TYPES.includes(itrType)) {
      next.itrType = 'Select an ITR type'
    }
    return next
  }

  const markTouched = (field: string) => setTouched((prev) => new Set(prev).add(field))

  const handleSubmit = async () => {
    if (!canSubmit) return
    const validation = validate()
    setErrors(validation)
    setTouched(new Set(['financialYear', 'itrType']))
    if (Object.keys(validation).length > 0) return

    setIsSaving(true)
    try {
      const svc = getAssessmentService()
      let saved: Assessment
      if (isEdit && assessment) {
        saved = await svc.update(assessment.id, { taxRegime })
        toast.success('Assessment updated', `${itrType} · FY ${financialYear}`)
      } else {
        saved = await svc.create({
          customerId,
          pan: customerPan,
          financialYear,
          assessmentYear,
          itrType,
          taxRegime,
          dueDate: dueDate || undefined,
        })
        toast.success('Assessment created', `${itrType} · FY ${financialYear}`)
      }
      onSuccess?.(saved)
      onClose()
    } catch (err) {
      toast.error(
        isEdit ? 'Could not update assessment' : 'Could not create assessment',
        err instanceof Error ? err.message : 'Please try again.'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const fyOptions = recentFinancialYears()
  // Ensure the seeded FY (from an edited assessment) is selectable.
  if (financialYear && !fyOptions.includes(financialYear)) {
    fyOptions.unshift(financialYear)
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="assessment-modal-title"
    >
      <div className="bg-white dark:bg-surface-dark rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-surface-overlay">
          <h2 id="assessment-modal-title" className="text-xl font-semibold text-text-primary">
            {isEdit ? 'Edit Assessment' : 'Start New Assessment'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-circle btn-sm"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <p className="text-sm text-text-secondary">
            PAN{' '}
            <code className="font-mono bg-surface-overlay px-2 py-0.5 rounded">{customerPan}</code>
          </p>

          {/* Financial year + ITR type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="assessment-fy"
                className="block text-sm font-medium text-text-primary mb-1"
              >
                Financial Year <span className="text-red-500">*</span>
              </label>
              <select
                id="assessment-fy"
                value={financialYear}
                disabled={isEdit}
                onChange={(e) => setFinancialYear(e.target.value)}
                onBlur={() => markTouched('financialYear')}
                className={`input input-bordered w-full ${
                  touched.has('financialYear') && errors.financialYear ? 'border-red-500' : ''
                } ${isEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {fyOptions.map((fy) => (
                  <option key={fy} value={fy}>
                    FY {fy}
                  </option>
                ))}
              </select>
              {touched.has('financialYear') && errors.financialYear && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" /> {errors.financialYear}
                </p>
              )}
              {assessmentYear && (
                <p className="text-xs text-text-tertiary mt-1">Assessment Year {assessmentYear}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="assessment-itr"
                className="block text-sm font-medium text-text-primary mb-1"
              >
                ITR Type <span className="text-red-500">*</span>
              </label>
              <select
                id="assessment-itr"
                value={itrType}
                disabled={isEdit}
                onChange={(e) => setItrType(e.target.value as ITRType)}
                onBlur={() => markTouched('itrType')}
                className={`input input-bordered w-full ${
                  touched.has('itrType') && errors.itrType ? 'border-red-500' : ''
                } ${isEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {ITR_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {touched.has('itrType') && errors.itrType && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" /> {errors.itrType}
                </p>
              )}
            </div>
          </div>

          {/* Tax regime */}
          <div>
            <span className="block text-sm font-medium text-text-primary mb-1">Tax Regime</span>
            <div className="flex gap-2">
              {(['new', 'old'] as TaxRegime[]).map((regime) => (
                <button
                  key={regime}
                  type="button"
                  onClick={() => setTaxRegime(regime)}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium border transition ${
                    taxRegime === regime
                      ? 'bg-primary-100 text-primary-700 border-primary-500 dark:bg-primary-900/30 dark:text-primary-400'
                      : 'border-surface-overlay text-text-secondary hover:bg-surface-overlay'
                  }`}
                >
                  {regime === 'new' ? 'New Regime' : 'Old Regime'}
                </button>
              ))}
            </div>
          </div>

          {/* Due date (optional) */}
          <div>
            <label
              htmlFor="assessment-due"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Due Date
            </label>
            <input
              id="assessment-due"
              type="date"
              value={dueDate}
              disabled={isEdit}
              onChange={(e) => setDueDate(e.target.value)}
              className={`input input-bordered w-full ${isEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
            />
            <p className="text-xs text-text-tertiary mt-1">
              Leave blank to default to 31 Jul of the assessment year.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-surface-overlay">
          <button type="button" onClick={onClose} className="btn btn-ghost">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="btn btn-primary flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <span className="loading loading-spinner loading-sm" />
                Saving…
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {isEdit ? 'Save Changes' : 'Create Assessment'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
